import React, { useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/hooks/useToast';
import { stockService } from '@/services/stockService';
import { productService } from '@/services/productService';
import { lotService } from '@/services/lotService';
import { useAuth } from '@/hooks/useAuth';
import { Check, AlertTriangle, X, Trash2, Mic, Plus, Calendar } from 'lucide-react';
import type { ParsedVoiceItem } from '@/services/voiceParserService';
import type { Product, ProductCategory, ProductUnit } from '@/types/product.types';
import type { ZoneStockage } from '@/types/inventory.types';

const CATEGORIES: { value: ProductCategory; label: string }[] = [
  { value: 'Légumes', label: 'Légumes' },
  { value: 'Viandes', label: 'Viandes' },
  { value: 'Poissons', label: 'Poissons' },
  { value: 'Laitages', label: 'Laitages' },
  { value: 'Fruits', label: 'Fruits' },
  { value: 'Épices', label: 'Épices' },
  { value: 'Condiments', label: 'Condiments' },
  { value: 'Conserves', label: 'Conserves' },
  { value: 'Produits secs', label: 'Produits secs' },
  { value: 'Huiles et vinaigres', label: 'Huiles et vinaigres' },
  { value: 'Pâtes et riz', label: 'Pâtes et riz' },
  { value: 'Farines et céréales', label: 'Farines et céréales' },
  { value: 'Boissons non alcoolisées', label: 'Boissons non alcoolisées' },
  { value: 'Alcools', label: 'Alcools' },
  { value: 'Vins', label: 'Vins' },
  { value: 'Bières', label: 'Bières' },
  { value: 'Produits d\'entretien', label: 'Produits d\'entretien' },
  { value: 'Produits d\'hygiène', label: 'Produits d\'hygiène' },
  { value: 'Emballages', label: 'Emballages' },
  { value: 'Matériel jetable', label: 'Matériel jetable' },
  { value: 'Autres', label: 'Autres' },
];

const UNITS: { value: ProductUnit; label: string }[] = [
  { value: 'kg', label: 'Kilogrammes (kg)' },
  { value: 'g', label: 'Grammes (g)' },
  { value: 'L', label: 'Litres (L)' },
  { value: 'mL', label: 'Millilitres (mL)' },
  { value: 'unité', label: 'Unités' },
];

interface VoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  transcript: string;
  parsedItems: ParsedVoiceItem[];
  products: Product[];
  onItemsUpdated: (items: ParsedVoiceItem[]) => void;
  onStockAdded: () => void;
  onProductCreated?: (product: Product) => void;
}

interface CreateProductForm {
  itemId: string;
  nom: string;
  categorie: ProductCategory;
  unite: ProductUnit;
}

const ZONES: { value: ZoneStockage; label: string }[] = [
  { value: 'positive', label: 'Zone Positive' },
  { value: 'negative', label: 'Zone Négative' },
  { value: 'frigo', label: 'Frigo' },
  { value: 'reserve', label: 'Réserve' },
  { value: 'cave', label: 'Cave' },
  { value: 'entretien', label: 'Entretien' },
];

export const VoiceModal: React.FC<VoiceModalProps> = ({
  isOpen,
  onClose,
  transcript,
  parsedItems,
  products,
  onItemsUpdated,
  onStockAdded,
  onProductCreated,
}) => {
  const { currentRestaurantId } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createForm, setCreateForm] = useState<CreateProductForm | null>(null);
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [localProducts, setLocalProducts] = useState<Product[]>(products);

  // Synchroniser les produits locaux quand les props changent
  React.useEffect(() => {
    setLocalProducts(products);
  }, [products]);

  const mapVoiceUnitToProductUnit = (voiceUnit: string): ProductUnit => {
    const mapping: Record<string, ProductUnit> = {
      'kg': 'kg',
      'g': 'g',
      'L': 'L',
      'mL': 'mL',
      'unité': 'unité',
    };
    return mapping[voiceUnit] || 'unité';
  };

  const startCreateProduct = (item: ParsedVoiceItem) => {
    setCreateForm({
      itemId: item.id,
      nom: item.productName.charAt(0).toUpperCase() + item.productName.slice(1),
      categorie: 'Autres',
      unite: mapVoiceUnitToProductUnit(item.unit),
    });
  };

  const cancelCreateProduct = () => {
    setCreateForm(null);
  };

  const handleCreateProduct = async () => {
    if (!createForm || !currentRestaurantId) return;

    setIsCreatingProduct(true);

    try {
      const result = await productService.createProduct(currentRestaurantId, {
        nom: createForm.nom,
        categorie: createForm.categorie,
        unite: createForm.unite,
        seuil_critique: 5,
        seuil_alerte: 10,
      });

      if (result.success && result.data) {
        const newProduct = result.data;

        // Ajouter le produit à la liste locale
        setLocalProducts((prev) => [...prev, newProduct]);

        // Notifier le parent
        onProductCreated?.(newProduct);

        // Mettre à jour l'item avec le nouveau produit
        onItemsUpdated(
          parsedItems.map((item) =>
            item.id === createForm.itemId
              ? {
                  ...item,
                  matchedProduct: newProduct,
                  status: 'matched' as const,
                  matchScore: 1,
                }
              : item
          )
        );

        toast({
          title: 'Produit créé',
          description: `"${newProduct.nom}" a été ajouté à votre catalogue`,
        });

        setCreateForm(null);
      } else {
        toast({
          title: 'Erreur',
          description: result.error || 'Impossible de créer le produit',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Erreur création produit:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingProduct(false);
    }
  };

  const handleQuantityChange = (itemId: string, quantity: number) => {
    onItemsUpdated(
      parsedItems.map((item) =>
        item.id === itemId ? { ...item, quantity: Math.max(0, quantity) } : item
      )
    );
  };

  const handleProductChange = (itemId: string, productId: string) => {
    const product = localProducts.find((p) => p.id === productId);
    if (!product) return;

    onItemsUpdated(
      parsedItems.map((item) =>
        item.id === itemId
          ? {
              ...item,
              matchedProduct: product,
              status: 'matched' as const,
              matchScore: 1,
            }
          : item
      )
    );
  };

  const handleZoneChange = (itemId: string, zone: ZoneStockage) => {
    onItemsUpdated(
      parsedItems.map((item) =>
        item.id === itemId ? { ...item, zone } : item
      )
    );
  };

  const handleDlcChange = (itemId: string, dlc: string) => {
    onItemsUpdated(
      parsedItems.map((item) =>
        item.id === itemId ? { ...item, dlc: dlc || null } : item
      )
    );
  };

  const handleRemoveItem = (itemId: string) => {
    onItemsUpdated(parsedItems.filter((item) => item.id !== itemId));
  };

  const handleSubmit = async () => {
    if (!currentRestaurantId) {
      toast({
        title: 'Erreur',
        description: 'Restaurant non trouvé',
        variant: 'destructive',
      });
      return;
    }

    // Vérifier que tous les items sont valides
    const validItems = parsedItems.filter(
      (item) => item.matchedProduct && item.zone && item.quantity > 0
    );

    if (validItems.length === 0) {
      toast({
        title: 'Aucun produit valide',
        description: 'Veuillez sélectionner des produits et leurs zones',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let successCount = 0;
      let errorCount = 0;
      let lotsCreated = 0;

      for (const item of validItems) {
        // Utiliser addToStock pour ajouter à la quantité existante ou créer si n'existe pas
        const result = await stockService.addToStock(
          currentRestaurantId,
          item.matchedProduct!.id,
          item.zone!,
          item.quantity
        );

        if (result.success) {
          successCount++;

          // Si une DLC est spécifiée, créer un lot
          if (item.dlc) {
            const numeroLot = item.numeroLot ||
              `LOT-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

            try {
              await lotService.createLot(currentRestaurantId, {
                produit_id: item.matchedProduct!.id,
                numero_lot: numeroLot,
                dlc: item.dlc,
                quantite_initiale: item.quantity,
                zone: item.zone!,
                date_reception: new Date().toISOString().split('T')[0],
              });
              lotsCreated++;
            } catch (lotError) {
              console.error(`Erreur création lot pour ${item.productName}:`, lotError);
            }
          }
        } else {
          errorCount++;
          console.error(`Erreur pour ${item.productName}:`, result.error);
        }
      }

      if (successCount > 0) {
        let description = `${successCount} produit${successCount > 1 ? 's' : ''} ajouté${successCount > 1 ? 's' : ''} au stock`;
        if (lotsCreated > 0) {
          description += ` (${lotsCreated} lot${lotsCreated > 1 ? 's' : ''} créé${lotsCreated > 1 ? 's' : ''})`;
        }
        toast({
          title: 'Stock ajouté',
          description,
        });
      }

      if (errorCount > 0) {
        toast({
          title: 'Erreurs',
          description: `${errorCount} produit${errorCount > 1 ? 's' : ''} n'ont pas pu être ajoutés`,
          variant: 'destructive',
        });
      }

      onStockAdded();
    } catch (error) {
      console.error('Erreur lors de l\'ajout au stock:', error);
      toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de l\'ajout au stock',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const validItemsCount = parsedItems.filter(
    (item) => item.matchedProduct && item.zone && item.quantity > 0
  ).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Saisie vocale"
      size="lg"
    >
      <div className="space-y-6">
        {/* Transcription originale */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Mic size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">
              Transcription
            </span>
          </div>
          <p className="text-gray-600 italic">"{transcript}"</p>
        </div>

        {/* Liste des produits parsés */}
        {parsedItems.length > 0 ? (
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">
              Produits détectés ({parsedItems.length})
            </h3>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {parsedItems.map((item) => (
                <div
                  key={item.id}
                  className="border rounded-lg p-4 bg-white space-y-3"
                >
                  {/* Header avec statut */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.status === 'matched' && (
                        <Badge variant="success" size="sm">
                          <Check size={12} />
                          Trouvé
                        </Badge>
                      )}
                      {item.status === 'ambiguous' && (
                        <Badge variant="warning" size="sm">
                          <AlertTriangle size={12} />
                          À vérifier
                        </Badge>
                      )}
                      {item.status === 'not_found' && (
                        <Badge variant="danger" size="sm">
                          <X size={12} />
                          Non trouvé
                        </Badge>
                      )}
                      {item.dlc && (
                        <Badge variant="neutral" size="sm" className="bg-amber-100 text-amber-800">
                          <Calendar size={12} />
                          DLC détectée
                        </Badge>
                      )}
                      <span className="text-sm text-gray-500">
                        "{item.rawText}"
                      </span>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(item.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {/* Sélection du produit */}
                  <div className="space-y-3">
                    {/* Ligne 1: Produit */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">
                        Produit
                      </label>
                      <select
                        value={item.matchedProduct?.id || ''}
                        onChange={(e) =>
                          handleProductChange(item.id, e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none bg-white"
                      >
                        <option value="">Sélectionner un produit</option>
                        {/* Suggestions en premier */}
                        {item.suggestedProducts.length > 0 && (
                          <optgroup label="Suggestions">
                            {item.suggestedProducts.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.nom}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {/* Tous les produits */}
                        <optgroup label="Tous les produits">
                          {localProducts.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.nom}
                            </option>
                          ))}
                        </optgroup>
                      </select>

                      {/* Bouton créer produit si non trouvé */}
                      {!item.matchedProduct && createForm?.itemId !== item.id && (
                        <button
                          type="button"
                          onClick={() => startCreateProduct(item)}
                          className="mt-2 flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-medium"
                        >
                          <Plus size={14} />
                          Créer "{item.productName}"
                        </button>
                      )}

                      {/* Formulaire de création de produit */}
                      {createForm?.itemId === item.id && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                          <p className="text-xs font-medium text-blue-800">
                            Créer un nouveau produit
                          </p>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">
                              Nom
                            </label>
                            <input
                              type="text"
                              value={createForm.nom}
                              onChange={(e) =>
                                setCreateForm({ ...createForm, nom: e.target.value })
                              }
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-primary focus:outline-none"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">
                                Catégorie
                              </label>
                              <select
                                value={createForm.categorie}
                                onChange={(e) =>
                                  setCreateForm({
                                    ...createForm,
                                    categorie: e.target.value as ProductCategory,
                                  })
                                }
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-primary focus:outline-none"
                              >
                                {CATEGORIES.map((cat) => (
                                  <option key={cat.value} value={cat.value}>
                                    {cat.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">
                                Unité
                              </label>
                              <select
                                value={createForm.unite}
                                onChange={(e) =>
                                  setCreateForm({
                                    ...createForm,
                                    unite: e.target.value as ProductUnit,
                                  })
                                }
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:border-primary focus:outline-none"
                              >
                                {UNITS.map((unit) => (
                                  <option key={unit.value} value={unit.value}>
                                    {unit.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="flex gap-2 pt-1">
                            <button
                              type="button"
                              onClick={cancelCreateProduct}
                              disabled={isCreatingProduct}
                              className="flex-1 px-2 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                            >
                              Annuler
                            </button>
                            <button
                              type="button"
                              onClick={handleCreateProduct}
                              disabled={isCreatingProduct || !createForm.nom.trim()}
                              className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-primary rounded hover:bg-primary-dark disabled:opacity-50"
                            >
                              {isCreatingProduct ? 'Création...' : 'Créer'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Ligne 2: Quantité + Zone */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Quantité ({item.unit})
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.quantity}
                          onChange={(e) =>
                            handleQuantityChange(
                              item.id,
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none bg-white"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Zone
                        </label>
                        <select
                          value={item.zone || ''}
                          onChange={(e) =>
                            handleZoneChange(
                              item.id,
                              e.target.value as ZoneStockage
                            )
                          }
                          className="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none bg-white"
                        >
                          <option value="">Sélectionner une zone</option>
                          {ZONES.map((zone) => (
                            <option key={zone.value} value={zone.value}>
                              {zone.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Ligne 3: DLC (optionnel) */}
                    <div>
                      <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
                        <Calendar size={12} />
                        DLC (optionnel)
                      </label>
                      <input
                        type="date"
                        value={item.dlc || ''}
                        onChange={(e) => handleDlcChange(item.id, e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none bg-white ${
                          item.dlc ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
                        }`}
                      />
                      {item.dlc && (
                        <p className="text-xs text-amber-600 mt-1">
                          Un lot sera créé avec cette DLC
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Aucun produit détecté dans la transcription
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center pt-4 border-t">
          <span className="text-sm text-gray-500">
            {validItemsCount} produit{validItemsCount > 1 ? 's' : ''} prêt
            {validItemsCount > 1 ? 's' : ''} à ajouter
          </span>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={validItemsCount === 0 || isSubmitting}
              loading={isSubmitting}
            >
              Ajouter au stock
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
